import React, { FC } from 'react';
import { Box, styled, Typography } from '@mui/material';
import {
  LocalizationProvider,
  StaticDatePicker,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

export type DayRange = [Dayjs | null, Dayjs | null];

interface Props {
  value: DayRange;
  onChange: (range: DayRange) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  label?: string;
  caption?: string;
  disabled?: boolean;
  /**
   * FRFD exact mode: the window is one decided day, so a pick closes both ends
   * at once and the value is always [day, day]. Day painting needs no change —
   * with start === end the slotProps.day branch below already scores isStart
   * AND isEnd true and `between` false, so exactly one day gets the solid pill.
   */
  singleDate?: boolean;
}

const StyledDatePicker = styled(StaticDatePicker)({
  border: '1px solid var(--border)',
  borderRadius: '16px 16px 0px 0px',
  width: '100%',
  minWidth: 280,
  '&.MuiPickersLayout-root': { minWidth: 'auto' },
  '& .MuiPickersToolbar-root': { display: 'none' },
  '& .MuiDateCalendar-root': { width: 310 },
});

export const ExpertRangeCalendar: FC<Props> = ({
  value,
  onChange,
  minDate,
  maxDate,
  label,
  caption,
  disabled,
  singleDate = false,
}) => {
  const [start, end] = value;
  const resolvedLabel =
    label ?? (singleDate ? 'Select Filming Date' : 'Select Filming Date Range');

  const handlePick = (picked: Dayjs | null) => {
    if (!picked) return;
    const day = picked.startOf('day');
    // One decided day: close both ends on the single tap. The start/end walk
    // below would leave the end null, which reads as a half-finished range.
    if (singleDate) {
      onChange([day, day]);
      return;
    }
    if (!start || (start && end) || day.isBefore(start, 'day')) {
      onChange([day, null]);
      return;
    }
    onChange([start, day]);
  };

  const summary = (() => {
    if (singleDate) {
      return start ? start.format('MMM DD, YYYY') : 'Select the filming date';
    }
    if (start && end) {
      // A one-day range reads as a single date, not "X - X".
      return start.isSame(end, 'day')
        ? start.format('MMM DD, YYYY')
        : `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
    }
    if (start) return `${start.format('MMM DD, YYYY')} - select an end date`;
    return 'Select a start date';
  })();

  return (
    <Box className="flex flex-col gap-3 w-full items-center">
      {resolvedLabel && (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1,
            color: '#999',
            textTransform: 'uppercase',
            alignSelf: 'flex-start',
          }}
        >
          {resolvedLabel}
        </Typography>
      )}
      <Box className="flex flex-col w-full">
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-us">
          <StyledDatePicker
            disabled={disabled}
            value={end || start || null}
            minDate={minDate || dayjs()}
            maxDate={maxDate}
            onChange={handlePick as (value: unknown) => void}
            slotProps={{
              actionBar: { actions: [] },
              day: (ownerState: any) => {
                const d: Dayjs | undefined = ownerState?.day;
                // StaticDatePicker suppresses onChange when the clicked day
                // already IS its value, which made "click the same day twice"
                // for a one-day range a no-op. onDaySelect fires every click.
                const props: Record<string, unknown> = d
                  ? { onDaySelect: (clicked: Dayjs) => handlePick(clicked) }
                  : {};
                if (!d || !start) return props;
                const isStart = d.isSame(start, 'day');
                const isEnd = !!end && d.isSame(end, 'day');
                const between =
                  !!end && d.isAfter(start, 'day') && d.isBefore(end, 'day');
                if (isStart || isEnd) {
                  props.sx = {
                    backgroundColor: '#A58450 !important',
                    color: '#fff !important',
                    '&:hover': { backgroundColor: '#8C7041 !important' },
                  };
                } else if (between) {
                  // #F2EBE2 IS the light value of --tint; hardcoding it made
                  // every in-range day an opaque cream disc, so the day
                  // numbers (text.primary #EAEDF2 in dark) vanished on it.
                  props.sx = {
                    backgroundColor: 'var(--tint)',
                    borderRadius: '50%',
                  };
                }
                return props;
              },
            }}
          />
        </LocalizationProvider>
        <Box className="flex flex-col items-center justify-center bg-surface-2 rounded-lg rounded-tr-none rounded-tl-none p-4 border border-t-0 border-border-token">
          <Typography variant="body1">{summary}</Typography>
          {caption && (
            <Typography
              sx={{ fontSize: 11, color: '#8A8A8A', mt: 0.5, textAlign: 'center' }}
            >
              {caption}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
